import axios from "../axios";
import { getHseDocuments } from "./documents";
import { fetchHseTiltak } from "./tiltak";
import {
  composeFallbackDashboard,
  HSE_DASHBOARD_PATHS,
  normalizeHseDashboard,
  shouldUseDashboardFallback,
} from "../../components/dashboard/hse/hseDashboardUtils";

function unwrapAxios(response) {
  return response?.data;
}

function axiosStatus(error) {
  return error?.response?.status;
}

function fulfilledValue(result) {
  return result.status === "fulfilled" ? result.value : null;
}

async function tryPrimaryDashboard() {
  let lastError = null;
  for (const path of HSE_DASHBOARD_PATHS) {
    try {
      const response = await axios.get(path);
      return { ok: true, path, payload: unwrapAxios(response) };
    } catch (error) {
      lastError = error;
      const status = axiosStatus(error);
      if (status && !shouldUseDashboardFallback(status) && status !== 500) {
        throw error;
      }
    }
  }
  return { ok: false, error: lastError };
}

export async function composeHseDashboardFromExisting({ currentUserId } = {}) {
  const actionParams = {
    page: 1,
    per_page: 20,
    sort_by: "deadline",
    sort_order: "asc",
  };
  if (currentUserId) actionParams.responsible_user_id = currentUserId;

  const [
    eventsResult,
    eventStatsResult,
    actionsResult,
    actionStatsResult,
    deviationsResult,
    documentsResult,
  ] = await Promise.allSettled([
    axios.get("/hse_events", {
      params: { page: 1, per_page: 8, sort_by: "event_date", sort_order: "desc" },
    }),
    axios.get("/hse_events/statistics"),
    fetchHseTiltak(actionParams),
    axios.get("/hse/tiltak/statistics"),
    axios.get("/quality/deviations", {
      params: {
        page: 1,
        per_page: 1,
        filters: { 0: { name: "status", value: JSON.stringify(["1", "2"]) } },
      },
    }),
    getHseDocuments({ status: "approved" }),
  ]);

  const eventsPayload = unwrapAxios(fulfilledValue(eventsResult));
  const eventStatsPayload = unwrapAxios(fulfilledValue(eventStatsResult));
  const actionsPayload = fulfilledValue(actionsResult);
  const actionStatsPayload = unwrapAxios(fulfilledValue(actionStatsResult));
  const deviationsPayload = unwrapAxios(fulfilledValue(deviationsResult));
  const documentsPayload = fulfilledValue(documentsResult);

  const failed = [
    eventsResult,
    eventStatsResult,
    actionsResult,
    deviationsResult,
    documentsResult,
  ].some((result) => result.status === "rejected");

  return {
    failed,
    data: composeFallbackDashboard({
      events: eventsPayload?.data || [],
      eventsTotal: eventsPayload?.meta?.total,
      eventStats: eventStatsPayload?.data || eventStatsPayload || {},
      actions: actionsPayload?.data || [],
      actionStats: actionStatsPayload?.data || actionStatsPayload || {},
      deviationsTotal: deviationsPayload?.totalCount ?? deviationsPayload?.meta?.total ?? 0,
      documents: documentsPayload?.documents || documentsPayload?.data || [],
    }),
  };
}

/**
 * Prefer GET /hse/dashboard (or /hse_ks/dashboard). On 404/403/501, compose
 * the same contract from existing HSE/deviation/document endpoints.
 */
export async function fetchHseDashboard({ currentUserId } = {}) {
  const primary = await tryPrimaryDashboard();
  if (primary.ok) {
    return {
      source: "api",
      path: primary.path,
      data: normalizeHseDashboard(primary.payload),
      failed: false,
    };
  }

  const fallback = await composeHseDashboardFromExisting({ currentUserId });
  const primaryStatus = axiosStatus(primary.error);
  return {
    source: "fallback",
    path: null,
    data: fallback.data,
    failed: fallback.failed,
    primaryMissing: shouldUseDashboardFallback(primaryStatus),
    primaryStatus,
  };
}
